import {
  Article,
  ChatBubble,
  DateRange,
  Groups,
  Home,
  PersonSearch,
} from "@mui/icons-material";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { Box, Chip, Drawer, Grid, List } from "@mui/material";
import { withStyles } from "@mui/styles";

import { useRouter } from "next/router";
import { useMemo, useRef } from "react";

import { Exec as ExecIcon } from "components/icons";
import ExpertPanelQuote from "components/icons/ExpertPanelQuote";
import NavigationHintIndicator from "components/navigation/NavigationHintIndicator";
import NavigationMenuItem from "components/navigation/NavigationMenuItem";
import { useLayout } from "components/shared/AppLayout";
import useFlags from "hooks/useFlags";
import { useChannelDMs } from "lib/chat-state/hooks";

import useStyles from "./styles";

const StyledChip = withStyles(() => ({
  root: {
    color: "#fff",
    backgroundColor: "#f44336",
    fontSize: "12px",
    height: "20px",
  },
  label: {
    padding: "0 6.5px",
    fontWeight: 600,
    fontSize: "11px",
  },
}))(Chip);

function Nav({ type }) {
  const classes = useStyles();
  const router = useRouter();
  const navRef = useRef(null);

  const { channels } = useChannelDMs();
  const {
    permExpertPanels,
    permGroups,
    permPublishing,
    permEvents,
    permReferMemberCopy,
  } = useFlags();

  const unreadCount = useMemo(
    () =>
      channels.reduce((totalUnreadMessages, channel) => {
        if (channel?.unreadCount > 0) {
          return totalUnreadMessages + channel.unreadCount;
        }
        return totalUnreadMessages;
      }, 0),
    [channels]
  );

  const shownUnreadCount = unreadCount > 99 ? "99+" : unreadCount;

  return (
    <nav ref={navRef} className={classes.drawer}>
      <NavigationHintIndicator searchElement={navRef} />
      <div className={classes.drawerContainer}>
        <Box px={2.5}>
          <List className={classes.linkList} component="nav">
            <NavigationMenuItem
              className={classes.navItem}
              href="/"
              icon={<Home />}
              id={`pendo_${type}-menu-item-home`}
              selected={router.pathname === "/"}
              title="Home"
            />

            {permPublishing === true && (
              <NavigationMenuItem
                className={classes.navItem}
                href="/publishing"
                icon={<Article />}
                id={`pendo_${type}-menu-item-publishing`}
                selected={router.pathname.startsWith("/publishing")}
                title="Publishing"
              />
            )}

            {permPublishing === false && permExpertPanels === true && (
              <NavigationMenuItem
                className={classes.navItem}
                href="/publishing/expert-panels"
                icon={<ExpertPanelQuote />}
                id={`pendo_${type}-menu-item-expert-panels`}
                selected={router.pathname.startsWith(
                  "/publishing/expert-panels"
                )}
                title="Expert Panels"
              />
            )}

            {permGroups === true && (
              <NavigationMenuItem
                className={classes.navItem}
                href="/groups?view=AllGroupsList"
                icon={<Groups />}
                id={`pendo_${type}-menu-item-groups`}
                selected={router.pathname.startsWith("/groups")}
                title="Groups"
              />
            )}

            <NavigationMenuItem
              className={classes.navItem}
              href="/messages"
              icon={<ChatBubble />}
              id={`pendo_${type}-menu-item-messages`}
              secondaryAction={
                unreadCount > 0 && <StyledChip label={shownUnreadCount} />
              }
              selected={router.pathname.startsWith("/messages")}
              title="Messages"
            />

            {permEvents === true && (
              <NavigationMenuItem
                className={classes.navItem}
                href="/events"
                icon={<DateRange />}
                id={`pendo_${type}-menu-item-events`}
                selected={router.pathname.startsWith("/events")}
                title="Events"
              />
            )}

            <NavigationMenuItem
              className={classes.navItem}
              href="/members"
              icon={<PersonSearch />}
              id={`pendo_${type}-menu-item-members`}
              selected={router.pathname.startsWith("/members")}
              title="Member directory"
            />

            <NavigationMenuItem
              className={classes.navItem}
              href="/exec"
              icon={<ExecIcon />}
              id={`pendo_${type}-menu-item-exec`}
              selected={router.pathname.startsWith("/exec")}
              title="EXEC perks"
            />

            {permReferMemberCopy?.confirmation && (
              <NavigationMenuItem
                className={classes.navItem}
                href="/refer"
                icon={<HandshakeIcon />}
                id={`pendo_${type}-menu-item-refer`}
                selected={router.pathname.startsWith("/refer")}
                title="Refer members"
              />
            )}
          </List>
        </Box>
      </div>
    </nav>
  );
}

export default function LeftNav() {
  const { drawerOpen, setDrawerOpen } = useLayout();

  return (
    <>
      <Grid item sx={{ display: { xs: "none", lg: "block" } }} xs={3}>
        <Nav type="desktop" />
      </Grid>

      <Drawer
        ModalProps={{
          keepMounted: true,
        }}
        open={drawerOpen}
        sx={{ display: { xs: "block", lg: "none" } }}
        variant="temporary"
        onClick={() => setDrawerOpen(false)}
        onClose={() => setDrawerOpen(false)}
      >
        <Nav type="mobile" />
      </Drawer>
    </>
  );
}
